import { supabase } from '../../lib/supabaseClient';
import {
  dossierHashHint,
  HASH_LINK_LIMIT_PER_KIND,
  HASH_LINK_MIN_CHARS,
  type HashLinkHit,
} from '../../lib/messaging/hashLink';


function escapeIlike(term: string): string {
  return term.replace(/\\/g, '\\\\').replace(/[%_]/g, '\\$&');
}

function sanitizeOrIlike(term: string): string {
  return escapeIlike(term).replace(/[,()]/g, ' ');
}

type DossierRow = {
  id: string;
  title: string;
  number: string | null;
  archived_at: string | null;
  closed_at: string | null;
};

type ContactRow = {
  id: string;
  kind: 'individual' | 'corporate';
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  email: string | null;
};

export async function searchHashLinks(term: string): Promise<HashLinkHit[]> {
  const q = term.trim();
  if (q.length < HASH_LINK_MIN_CHARS) return [];
  const s = sanitizeOrIlike(q);

  const [dossiersRes, contactsRes] = await Promise.all([
    supabase
      .from('dossiers')
      .select('id, title, number, archived_at, closed_at')
      .is('deleted_at', null)
      .or(`title.ilike.%${s}%,number.ilike.%${s}%`)
      .order('opened_at', { ascending: false })
      .limit(HASH_LINK_LIMIT_PER_KIND),
    supabase
      .from('contacts')
      .select('id, kind, first_name, last_name, company_name, email')
      .eq('kind', 'corporate')
      .or(`company_name.ilike.%${s}%,siret.ilike.%${s}%,email.ilike.%${s}%`)
      .order('company_name', { ascending: true, nullsFirst: false })
      .limit(HASH_LINK_LIMIT_PER_KIND),
  ]);

  if (dossiersRes.error) {
    throw new Error(`Failed to search dossiers: ${dossiersRes.error.message}`);
  }
  if (contactsRes.error) {
    throw new Error(`Failed to search sociétés: ${contactsRes.error.message}`);
  }

  const dossiers: HashLinkHit[] = ((dossiersRes.data ?? []) as DossierRow[]).map((row) => ({
    kind: 'dossier',
    id: row.id,
    title: row.title,
    subtitle: row.number ?? undefined,
    hint: dossierHashHint({ closedAt: row.closed_at, archivedAt: row.archived_at }),
  }));

  const societes: HashLinkHit[] = ((contactsRes.data ?? []) as ContactRow[]).map((row) => ({
    kind: 'societe' as const,
    id: row.id,
    title: row.company_name?.trim() || 'Société sans nom',
    subtitle: row.email ?? undefined,
    hint: null,
  }));

  return [...dossiers, ...societes];
}
